import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface MetroHealthStats {
  leads: {
    total: number;
    missingPostcode: number;
    missingMetro: number;
  };
  jobs: {
    total: number;
    missingPostcode: number;
    missingMetro: number;
  };
}

export function useMetroHealth() {
  return useQuery({
    queryKey: ['metro-health'],
    queryFn: async (): Promise<MetroHealthStats> => {
      // Get lead stats
      const { count: totalLeads } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true });

      const { count: leadsMissingPostcode } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .or('customer_postcode.is.null,customer_postcode.eq.');

      const { count: leadsMissingMetro } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .is('metro_id', null);

      // Get job stats
      const { count: totalJobs } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true });

      const { count: jobsMissingPostcode } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .or('customer_postcode.is.null,customer_postcode.eq.');

      const { count: jobsMissingMetro } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .is('metro_id', null);

      return {
        leads: {
          total: totalLeads || 0,
          missingPostcode: leadsMissingPostcode || 0,
          missingMetro: leadsMissingMetro || 0,
        },
        jobs: {
          total: totalJobs || 0,
          missingPostcode: jobsMissingPostcode || 0,
          missingMetro: jobsMissingMetro || 0,
        },
      };
    },
    staleTime: 60000, // 1 minute
  });
}

export function useBackfillMetroAssignments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('backfill_metro_assignments_haversine');

      if (error) {
        throw new Error(`Backfill failed: ${error.message}`);
      }

      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['metro-health'] });
      await queryClient.invalidateQueries({ queryKey: ['reports', 'metro-report'] });

      return data;
    },
    onSuccess: (data) => {
      const result = Array.isArray(data) ? data[0] : data;
      const msg = result
        ? `Assigned metros to ${result.leads_updated || 0} leads and ${result.jobs_updated || 0} jobs`
        : 'Metro assignments recalculated';
      toast.success(msg);
    },
    onError: (error: Error) => {
      toast.error(`Failed to recalculate: ${error.message}`);
    },
  });
}
